import {Active} from '@dnd-kit/core';
import {IconButton} from '@wandb/components';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import React, {useMemo} from 'react';

import {useSelector} from '../../../state/hooks';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {DEFAULT_PANEL_SIZE} from '../../../util/panelbankTypes';
import {getPanelSpec} from '../../../util/panelTypes';

/**
 * This is a presentational component that is used to display the drag overlay for a panel.
 * This should not be decoupled from the panel content.
 * https://docs.dndkit.com/api-documentation/draggable/drag-overlay#patterns
 */
export const WorkspacePanelDragOverlay = ({
  activeItem,
}: {
  activeItem: Active | null;
}) => {
  if (!activeItem || activeItem?.data.current?.type !== 'panel') {
    return null;
  }

  return (
    <div
      className="cursor-grabbing rounded border border-teal-450 bg-white px-12 py-4 shadow-lg"
      style={{
        height: DEFAULT_PANEL_SIZE,
        width: DEFAULT_PANEL_SIZE,
      }}>
      <div className="flex w-full items-center">
        <div className="flex w-full justify-between">
          <Button
            aria-label="Drag panel overlay"
            className="cursor-grab active:cursor-grabbing"
            icon="drag-grip"
            size="small"
            variant="ghost"
          />
          <div className="flex-end flex gap-4">
            <IconButton
              aria-label="Create alert automation"
              icon="bell-notifications"
              size="sm"
              variant="ghost"
            />
            <IconButton
              aria-label="View full screen"
              icon="full-screen-mode-expand"
              size="sm"
              variant="ghost"
            />
            <IconButton
              aria-label="Edit panel"
              icon="settings"
              size="sm"
              variant="ghost"
            />
            <IconButton
              aria-label="More panel actions"
              icon="overflow-horizontal"
              size="sm"
              variant="ghost"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center">
        <WorkspacePanelTitle panelRef={activeItem.data.current.panelRef} />
      </div>
    </div>
  );
};

const WorkspacePanelTitle = ({panelRef}: {panelRef: PanelRef}) => {
  const {viewType, config} = useSelector(state => {
    const panel = state.views.parts[panelRef.type][panelRef.id];
    return {
      viewType: panel?.viewType,
      config: panel?.config,
    };
  });

  const title = useMemo(() => {
    const {getTitleFromConfig} = getPanelSpec(viewType);
    return getTitleFromConfig?.(config as any);
  }, [viewType, config]);

  if (!viewType || !title) {
    return <div className="text-sm font-semibold text-moon-800">Panel</div>;
  }

  return (
    <div
      className="overflow-hidden text-sm font-semibold text-moon-800"
      style={{
        width: DEFAULT_PANEL_SIZE - 50,
        overflowWrap: 'anywhere',
        maxHeight: DEFAULT_PANEL_SIZE - 50,
      }}>
      {title}
    </div>
  );
};
